import {HoistProps, PlainObject, creates, hoistCmp} from '@xh/hoist/core';
import {hbox, hframe, span, vbox} from '@xh/hoist/cmp/layout';
import {button} from '@xh/hoist/desktop/cmp/button';
import {numberInput, select, switchInput} from '@xh/hoist/desktop/cmp/input';
import {Icon} from '@xh/hoist/icon';
import {popover} from '@xh/hoist/kit/blueprint';
import {IndicatorParam} from '../../../data/ChartTypes';
import {IndicatorParamsPickerModel} from './IndicatorParamsPickerModel';
import './IndicatorParamsPicker.scss';
import {compactToolbarButton} from '../../../cmp/toolbar/Toolbar';

interface IndicatorParamsPickerProps extends HoistProps<IndicatorParamsPickerModel> {
    /** Active indicator type, e.g. 'rsi'. */
    indicator: string;
    /** Current parameter values - anything omitted falls back to the indicator's own default. */
    params: PlainObject;
    onParamsChange?: (params: PlainObject) => void;
}

export const indicatorParamsPicker = hoistCmp.factory<IndicatorParamsPickerProps>({
    model: creates(IndicatorParamsPickerModel),
    render({model}) {
        return popover({
            interactionKind: 'click',
            targetProps: {className: 'square-button-target'},
            isOpen: model.isOpen,
            onInteraction: state => (state ? model.open() : model.close()),
            item: compactToolbarButton({
                icon: Icon.gear(),
                intent: model.isDefault ? null : 'primary',
                tooltip: `Configure ${model.indicatorLabel}`
            }),
            content: paramsForm()
        });
    }
});

const paramsForm = hoistCmp.factory<IndicatorParamsPickerModel>({
    render({model}) {
        const {paramSpecs} = model;

        return vbox({
            className: 'indicator-params',
            items: [
                hframe({
                    className: 'indicator-params__header',
                    items: [
                        span({className: 'indicator-params__title', item: model.indicatorLabel}),
                        button({
                            text: 'Reset',
                            minimal: true,
                            disabled: model.isDefault,
                            onClick: () => model.resetToDefaults()
                        })
                    ]
                }),
                ...(paramSpecs.length
                    ? paramSpecs.map(spec => paramField({spec}))
                    : [
                          span({
                              className: 'indicator-params__empty',
                              item: 'No configurable parameters.'
                          })
                      ])
            ]
        });
    }
});

interface ParamFieldProps extends HoistProps<IndicatorParamsPickerModel> {
    spec: IndicatorParam;
}

const paramField = hoistCmp.factory<ParamFieldProps>({
    render({model, spec}) {
        return hbox({
            className: 'indicator-params__field',
            items: [span({className: 'indicator-params__label', item: spec.label}), paramInput({spec})]
        });
    }
});

const paramInput = hoistCmp.factory<ParamFieldProps>({
    render({model, spec}) {
        const {name, type} = spec,
            value = model.values[name];

        switch (type) {
            case 'select':
                return select({
                    value,
                    options: spec.options,
                    width: 120,
                    enableFilter: false,
                    enableClear: false,
                    onCommit: v => model.setValue(name, v)
                });
            case 'boolean':
                return switchInput({
                    value: !!value,
                    onCommit: v => model.setValue(name, v)
                });
            case 'numberList':
                return hbox({
                    className: 'indicator-params__list',
                    items: Array.from({length: spec.length}, (_, idx) =>
                        numberInput({
                            value: value?.[idx],
                            width: 56,
                            selectOnFocus: true,
                            onCommit: v => model.setListValue(name, idx, v)
                        })
                    )
                });
            default:
                return numberInput({
                    value,
                    width: 120,
                    selectOnFocus: true,
                    onCommit: v => model.setValue(name, v)
                });
        }
    }
});
